import { useState } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Circle, Line, Path, Rect } from "react-native-svg";


export function Padlock({ closed, color, size = 16 }: { closed: boolean; color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Rect x={5} y={11} width={14} height={10} rx={2} stroke={color} strokeWidth={1.7} fill="none" />
      <Path
        d={closed ? "M8 11V8a4 4 0 0 1 8 0v3" : "M8 11V8a4 4 0 0 1 7.5-2"}
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        fill="none"
      />
      <Circle cx={12} cy={15.5} r={1.4} fill={color} />
      <Line x1={12} y1={16.6} x2={12} y2={18.4} stroke={color} strokeWidth={1.4} strokeLinecap="round" />
    </Svg>
  );
}

export function EyeIcon({ hidden, color, size = 16 }: { hidden: boolean; color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12Z"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <Circle cx={12} cy={12} r={2.6} stroke={color} strokeWidth={1.7} fill="none" />
      {hidden ? (
        <Line x1={4} y1={20} x2={20} y2={4} stroke={color} strokeWidth={1.9} strokeLinecap="round" />
      ) : null}
    </Svg>
  );
}

export function BellGlyph({ muted, color, size = 16 }: { muted: boolean; color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M6 8a6 6 0 1 1 12 0c0 5.6 1.8 7.4 2.6 8H3.4C4.2 15.4 6 13.6 6 8Z"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path d="M10.3 20a1.9 1.9 0 0 0 3.4 0" stroke={color} strokeWidth={1.6} strokeLinecap="round" />
      {muted ? (
        <Line x1={4} y1={20} x2={20} y2={4} stroke={color} strokeWidth={1.8} strokeLinecap="round" />
      ) : null}
    </Svg>
  );
}

export function LeaveIcon({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4"
        stroke={color}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <Path d="M9 16l4-4-4-4" stroke={color} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <Path d="M3 12h10" stroke={color} strokeWidth={1.7} strokeLinecap="round" />
    </Svg>
  );
}

export function TrashIcon({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path d="M4 7h16" stroke={color} strokeWidth={1.7} strokeLinecap="round" />
      <Path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" stroke={color} strokeWidth={1.7} strokeLinecap="round" fill="none" />
      <Path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" stroke={color} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <Line x1={10} y1={11} x2={10} y2={18} stroke={color} strokeWidth={1.5} strokeLinecap="round" />
      <Line x1={14} y1={11} x2={14} y2={18} stroke={color} strokeWidth={1.5} strokeLinecap="round" />
    </Svg>
  );
}

export function TeamIconButton({
  icon,
  label,
  description,
  onPress,
  active = false,
  destructive = false,
  disabled = false,
}: {
  icon: (color: string) => React.ReactNode;
  label: string;
  description?: string;
  onPress?: () => void;
  active?: boolean;
  destructive?: boolean;
  disabled?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const isWebHover = Platform.OS === "web" && hovered && !disabled;

  // These buttons live on the dark forest hero band, so the resting palette
  // is light translucent (visible on green) and hover/active flips to amber.
  const bg = destructive
    ? isWebHover ? "rgba(217,83,79,0.18)" : "rgba(217,83,79,0.08)"
    : active ? STUDENT_THEME.sidebarItemActiveBg
    : isWebHover ? STUDENT_THEME.surfaceDividerFaint : "rgba(247,244,238,0.04)";
  const border = destructive
    ? isWebHover ? "rgba(217,83,79,0.65)" : "rgba(217,83,79,0.40)"
    : active ? "rgba(181,135,58,0.65)"
    : isWebHover ? "rgba(247,244,238,0.45)" : "rgba(247,244,238,0.22)";
  const iconColor = destructive
    ? isWebHover ? "#E76F65" : "#D9534F"
    : active ? COLORS.AMBER.hex
    : isWebHover ? COLORS.AMBER.hex
    : "rgba(247,244,238,0.85)";

  const cursorWeb = Platform.OS === "web" && !disabled ? ({ cursor: "pointer" } as ViewStyle) : null;

  return (
    <View style={{ position: "relative" }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        disabled={disabled}
        onPress={disabled ? undefined : onPress}
        {...(Platform.OS === "web"
          ? ({ onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false) } as Record<string, () => void>)
          : {})}
        style={[
          {
            width: 34,
            height: 34,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: border,
            backgroundColor: bg,
            alignItems: "center",
            justifyContent: "center",
            opacity: disabled ? 0.4 : 1,
            transitionProperty: "background-color, border-color",
            transitionDuration: "180ms",
          } as unknown as ViewStyle,
          cursorWeb,
        ]}
      >
        {icon(iconColor)}
      </Pressable>
      {isWebHover ? (
        <View
          pointerEvents="none"
          style={{
            // Drop the tooltip BELOW the button and anchor from the RIGHT edge so
            // it stays visible when the buttons sit in the top-right of the page
            // (no overflow above the viewport, no overflow off the right side).
            position: "absolute",
            top: 44,
            right: 0,
            width: 260,
            backgroundColor: COLORS.FOREST.hex,
            borderWidth: 1,
            borderColor: STUDENT_THEME.accentBorderSoft,
            paddingHorizontal: 12,
            paddingVertical: 9,
            borderRadius: 6,
            zIndex: 100,
            boxShadow: "0 12px 28px -14px rgba(0,0,0,0.45)",
          } as unknown as ViewStyle}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.4,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
              marginBottom: 3,
            }}
          >
            {label}
          </Text>
          {description ? (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 11.5,
                lineHeight: 16,
                color: "rgba(247,244,238,0.78)",
              }}
            >
              {description}
            </Text>
          ) : null}
          {/* Diamond points UP from the top-right of the tooltip toward the button. */}
          <View
            style={{
              position: "absolute",
              right: 11,
              top: -6,
              transform: [{ rotate: "45deg" }],
              width: 12,
              height: 12,
              backgroundColor: COLORS.FOREST.hex,
              borderLeftWidth: 1,
              borderTopWidth: 1,
              borderColor: STUDENT_THEME.accentBorderSoft,
            }}
          />
        </View>
      ) : null}
    </View>
  );
}

export function VisibilityPill({ visibility }: { visibility: "public" | "private" }) {
  const isPrivate = visibility === "private";
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: isPrivate ? STUDENT_THEME.accentBorderHover : "rgba(28,43,26,0.18)",
        backgroundColor: isPrivate ? STUDENT_THEME.accentTintSoft : "rgba(28,43,26,0.04)",
        alignSelf: "flex-start",
      }}
    >
      <EyeIcon hidden={isPrivate} color={isPrivate ? COLORS.AMBER.hex : STUDENT_THEME.textOnSurfaceDim} size={13} />
      <Text
        style={{
          fontFamily: FONTS.bodySemiBold,
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: isPrivate ? COLORS.AMBER.hex : STUDENT_THEME.textOnSurfaceDim,
        }}
      >
        {isPrivate ? "Private" : "Public"}
      </Text>
    </View>
  );
}
